import { Button, Tooltip } from "@material-ui/core";
import { WarningRounded } from "@material-ui/icons";
import React, { useMemo, useState } from "react";

import { Event } from "../../../dtos/event";
import { useAuth } from "../../../features/auth/context";
import { getEventNameDescription } from "../../../utils/getEventNameDescription";
import { isEventUserDeleted } from "../../../utils/isEventUserDeleted";
import { getTimestamp } from "../../../utils/getTimestamp";
import { NotesEditField } from "../../shared/NotesEditField";
import { ResponsiveDataView, Column } from "../../shared/ResponsiveDataView";
import { ViewEmployeeLink } from "../../shared/ViewEmployee";
import { useApi } from "../../useApi";

import { AlarmAcknowledgementDialog } from "./AlarmAcknowledgementDialog";

export interface AlarmForDisplay {
    id: number;
    user: string;
    userId: number | null;
    isUserEmployee: boolean;
    isUserDeleted: boolean;
    accountType: string | null;
    timestamp: string | undefined;
    eventName: string | undefined;
    description: string;
    acknowledgeInfo: string | undefined;
    acknowledged: boolean;
    notesInfo: string | undefined;
    notes: string | null;
    uniqueSiteId: string | null;
    siteName: string | null;
    organizationName: string | null;
    wasInMaintenanceMode: boolean;
}

interface Props {
    events: Event[];
    filterByLocalTime: boolean | null;
    showAcknowledgeButtons: boolean;
    showSiteName: boolean;
    onAlarmNotesUpdated: () => void;
    isLoading?: boolean;
}

export function AlarmTable({
    events,
    filterByLocalTime,
    showAcknowledgeButtons,
    showSiteName,
    onAlarmNotesUpdated,
    isLoading,
}: Props) {
    const [eventToAcknowledgeId, setEventToAcknowledgeId] = useState<number | undefined>();
    const { eventApi } = useApi();
    const { isReadOnlyUser } = useAuth();

    const alarmsForDisplay: AlarmForDisplay[] = useMemo(
        () => {
            return events.map((e) => {
                const acknowledgedTimestamp = getTimestamp(filterByLocalTime, e.acknowledgedOnLocal, e.acknowledgedOnUtc);
                const notesEditedTimestamp = getTimestamp(filterByLocalTime, e.notesEditedOnLocal, e.notesEditedOnUtc);

                const acknowledgeInfo = e.acknowledged
                    ? `${e.acknowledgedByName ? ` by ${e.acknowledgedByName}` : ""
                    }${acknowledgedTimestamp ? ` ${acknowledgedTimestamp}` : ""
                    }`
                    : "No information available";

                const notesInfo = e.notes
                    ? `${e.notesEditedByName ? `last updated by ${e.notesEditedByName}` : ""
                    }${notesEditedTimestamp ? ` ${notesEditedTimestamp}` : ""
                    }`
                    : "No information available";

                return {
                    accountType: e.accountType,
                    description: e.description,
                    eventName: e.eventNameForDisplay || getEventNameDescription(e.eventType, e.eventName),
                    timestamp: getTimestamp(filterByLocalTime, e.localTimestamp, e.timestamp),
                    user: e.employeeName || e.customerName,
                    userId: e.employeeId || e.customerId,
                    id: e.id,
                    isUserEmployee: Boolean(e.employeeId),
                    isUserDeleted: isEventUserDeleted(e),
                    acknowledgeInfo: acknowledgeInfo,
                    acknowledged: e.acknowledged,
                    notesInfo: notesInfo,
                    notes: e.notes,
                    uniqueSiteId: e.uniqueSiteId,
                    siteName: e.siteName,
                    organizationName: e.organizationName,
                    wasInMaintenanceMode: Boolean(e.wasInMaintenanceMode),
                };
            });
        },
        [events, filterByLocalTime]
    );

    const maintenanceColor = (a: AlarmForDisplay) => (a.wasInMaintenanceMode ? "#f57c00" : undefined);

    const columns: Column<AlarmForDisplay>[] = [
        {
            key: 'uniqueSiteId',
            label: 'Site Id',
            primary: true,
            render: (a) => (
                <Tooltip title={`${a.organizationName ? a.organizationName + " - " : ""}${a.siteName}`}>
                    <span>{a.uniqueSiteId}</span>
                </Tooltip>
            ),
        },
        ...(showSiteName ? [{ key: 'siteName', label: 'Site Name', hideWhenEmpty: true } as Column<AlarmForDisplay>] : []),
        { key: 'user', label: 'User', hideWhenEmpty: true },
        {
            key: 'userId',
            label: 'User Id',
            hideWhenEmpty: true,
            render: (a) => (a.isUserEmployee
                ? <ViewEmployeeLink userId={a.userId} isUserDeleted={a.isUserDeleted} />
                : <span>{a.userId}</span>),
        },
        { key: 'accountType', label: 'Account Type', hideWhenEmpty: true },
        {
            key: 'timestamp',
            label: 'Timestamp',
            render: (a) => <span style={{ color: maintenanceColor(a) }}>{a.timestamp}</span>,
        },
        {
            key: 'eventName',
            label: 'Alarm',
            render: (a) => (
                <span style={{ color: maintenanceColor(a) }}>
                    {a.wasInMaintenanceMode && (
                        <Tooltip title="This alarm occurred during maintenance mode">
                            <WarningRounded style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "4px" }} />
                        </Tooltip>
                    )}
                    {a.eventName}
                </span>
            ),
        },
        {
            key: 'description',
            label: 'Description',
            render: (a) => <span style={{ color: maintenanceColor(a) }}>{a.description}</span>,
        },
        {
            key: 'acknowledged',
            label: 'Acknowledged',
            render: (a) => (a.acknowledged
                ? (
                    <Tooltip title={a.acknowledgeInfo} enterDelay={700}>
                        <span>ACKNOWLEDGED</span>
                    </Tooltip>
                )
                : showAcknowledgeButtons
                    ? (
                        <Button
                            variant="contained"
                            color="inherit"
                            size="small"
                            onClick={() => setEventToAcknowledgeId(a.id)}
                        >
                            Acknowledge
                        </Button>
                    )
                    : null),
        },
        {
            key: 'notes',
            label: 'Notes',
            render: (a) => (
                <NotesEditField
                    notes={a.notes}
                    updateNotesRequest={eventApi.updateAlarmNote}
                    entityId={a.id}
                    tooltipText={a.notesInfo}
                    onNotesUpdated={onAlarmNotesUpdated}
                    disabled={!a.acknowledged || isReadOnlyUser}
                />
            ),
        },
    ];

    return (
        <>
            <ResponsiveDataView
                rows={alarmsForDisplay}
                columns={columns}
                rowKey={(a) => `${a.id}_${a.eventName}`}
                loading={isLoading}
            />

            <AlarmAcknowledgementDialog
                open={eventToAcknowledgeId !== undefined}
                onSuccess={onAlarmNotesUpdated}
                onClose={() => setEventToAcknowledgeId(undefined)}
                event={events.find(it => it.id === eventToAcknowledgeId)}
            />
        </>
    );
}
